import { FieldPath, FieldValues, RegisterOptions, useController } from "react-hook-form";
import CheckboxGroup, { TCheckboxGroupProps } from "../checkbox-group/checkbox-group";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "./hook-form-helper";
import { THookFormFieldProps } from "./hook-form-types";
import { useEffect } from "react";
import { isArrayWithItems } from "../../../helpers";

export type THookFormCheckBoxGroupRules<T extends FieldValues = FieldValues> = Pick<
  RegisterOptions<T, FieldPath<T>>,
  "required"
>;

type THookFormCheckboxGroupProps<TFieldValues extends FieldValues> = THookFormFieldProps<TFieldValues> & {
  rules?: THookFormCheckBoxGroupRules<TFieldValues>;
} & Pick<TCheckboxGroupProps, "checkboxes" | "helperText" | "isDisabled" | "label">;

export const HookFormCheckboxGroup = <T extends FieldValues>({
  rules,
  control,
  name,
  label,
  helperText,
  checkboxes,
  isDisabled,
  defaultValue,
}: THookFormCheckboxGroupProps<T>) => {
  const { t } = useTranslation();

  const { field, fieldState } = useController({
    name: name,
    control: control,
    defaultValue: defaultValue,
    disabled: isDisabled,
    rules: rules,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (defaultValue) {
      field.onChange(defaultValue);
    }
  }, []);

  const { error } = fieldState;
  const { value } = field;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let valueCopy = isArrayWithItems(value) ? [...value] : [];
    if (event.target.checked && !valueCopy.includes(event.target.name)) {
      valueCopy.push(event.target.name);
    } else if (!event.target.checked && valueCopy.includes(event.target.name)) {
      valueCopy = valueCopy.filter((name) => name !== event.target.name);
    }
    field.onChange(valueCopy);
  };

  return (
    <CheckboxGroup
      checkboxes={checkboxes.map((checkbox) => {
        return {
          name: checkbox.name,
          isChecked: value && value.includes(checkbox.name),
          label: checkbox.label,
          value: checkbox.value,
        };
      })}
      onChangeNative={onChange}
      helperText={helperText}
      errorText={getErrorMessage(t, error)}
      label={label}
      isDisabled={isDisabled}
    />
  );
};
